// contexts/PopupContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
/**
* Target object describing the time edit details.
*/
export interface PopupTarget {
    /** Data for the popup */
    title: string,
    content: string,
    backBtnText?: string,
    submitBtnText?: string,
    onClose?: ()=>void,
    onSubmit?: ()=>void
}

/**
* Shape of the Popup Context value.
*/
interface PopupContextType {
    /** Whether the time modal is currently open */
    isOpen: boolean;
    /** Function to open the time modal */
    open: () => void;
    /** Function to close the time modal and reset target */
    close: () => void;
    /** Sets the current time edit target or null to clear */
    setPopupTarget: (target: PopupTarget | null) => void;
    /** Current target data for Popup editing, or null if none */
    popupTarget: PopupTarget | null;
    /** Whether the Popup is open over a differrent modal */
    openOnTop: boolean;
    /** Whether the Popup is open over a differrent modal setter*/
    setOpenOnTop: (val: boolean) => void;
}

/**
* React context to manage state and actions for a time editing modal.
* Provides modal open state, open/close methods, and the current time edit target.
*/
const PopupContext = createContext<PopupContextType | undefined>(undefined);

/**
* Provider component that manages time modal state and provides context to children.
* It also locks background scroll while the modal is open.
* 
* @param children - React child elements
*/
export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [ openOnTop, setOpenOnTop ] = useState<boolean>(false)
    const [isOpen, setIsOpen] = useState(false); // Modal open flag
    const [popupTarget, setPopupTarget] = useState<PopupTarget | null>(null);

    const open = () => setIsOpen(true); // Open time modal
    const close = () => { // Close time modal 
        setIsOpen(false);
        setPopupTarget(null);
    };
    // Stop scroll on open
    useEffect(()=>{
        // Handle only closing background scroll while open on top
        if(openOnTop && isOpen === false){
            setOpenOnTop(false);
            return;
        }
        handleBackgroundScroll(isOpen);
    }, [isOpen])

    return (
        <PopupContext.Provider value={{ isOpen, open, close, popupTarget, setPopupTarget, openOnTop, setOpenOnTop}}>
        {children}
        </PopupContext.Provider>
    );
};
/**
* Hook to access the PopupContext.
* Throws an error if used outside of a PopupProvider.
* 
* @returns PopupContextType object with modal state and control functions
*/
export const usePopup = () => {
    const context = useContext(PopupContext);
    if (!context) throw new Error("usePopup must be used within a PopupProvider");
    return context;
};